import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Save, ImagePlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useGetCategoriesQuery, useCreateCategoryMutation, useUpdateCategoryMutation, useGetCategoryByIdQuery } from "@/store";
import { toast } from "sonner";
import type { CreateCategoryData, UpdateCategoryData } from "@/store/categoriesApi";
import type { Media } from "@/types";
import { MediaSelector } from "@/components/media";

interface CategoryFormData extends CreateCategoryData {}

export function CategoryFormScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [mediaSelectorOpen, setMediaSelectorOpen] = useState(false);

  const { data: categoryData, isLoading: isLoadingCategory } = useGetCategoryByIdQuery(id || "", {
    skip: !id,
  });
  const { data: categoriesData } = useGetCategoriesQuery({});
  const [createCategory, { isLoading: isCreating }] = useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();

  const categories = categoriesData?.data || [];
  const currentCategory = categoryData?.data;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<CategoryFormData>({
    defaultValues: {
      name: "",
      slug: "",
      description: "",
      image: "",
      parentId: "none",
    },
  });

  const image = watch("image");
  const parentId = watch("parentId");

  // Generate slug from name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  // Handle name change to auto-generate slug
  const handleNameChange = (name: string) => {
    setValue("name", name);
    if (!isEditMode || !currentCategory) {
      setValue("slug", generateSlug(name));
    }
  };

  // Populate form when editing
  useEffect(() => {
    if (isEditMode && currentCategory) {
      reset({
        name: currentCategory.name,
        slug: currentCategory.slug,
        description: currentCategory.description || "",
        image: currentCategory.image || "",
        parentId: currentCategory.parentId || "none",
      });
    }
  }, [isEditMode, currentCategory, reset]);

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const processedData = {
        ...data,
        parentId: data.parentId === "none" ? undefined : data.parentId,
      };

      if (isEditMode && id) {
        const updateData: UpdateCategoryData = processedData;
        await updateCategory({ id, data: updateData }).unwrap();
        toast.success("Category updated successfully");
      } else {
        await createCategory(processedData).unwrap();
        toast.success("Category created successfully");
      }
      navigate("/admin/categories");
    } catch (error: any) {
      const errorMessage =
        error?.data?.error ||
        error?.data?.message ||
        `Failed to ${isEditMode ? "update" : "create"} category`;
      toast.error(errorMessage);
    }
  };

  const isLoading = isLoadingCategory || isCreating || isUpdating;

  if (isEditMode && isLoadingCategory) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading category...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/admin/categories")}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isEditMode ? "Edit Category" : "Create Category"}
            </h1>
            <p className="text-muted-foreground">
              {isEditMode
                ? "Update category information"
                : "Add a new category to organize your products"}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid gap-6 md:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Essential category details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">
                    Category Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    placeholder="e.g., Rice Dishes"
                    {...register("name", {
                      required: "Category name is required",
                      onChange: (e) => handleNameChange(e.target.value),
                    })}
                  />
                  {errors.name && (
                    <p className="text-sm text-destructive">{errors.name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="slug">
                    Slug <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="slug"
                    placeholder="e.g., rice-dishes"
                    {...register("slug", {
                      required: "Slug is required",
                      pattern: {
                        value: /^[a-z0-9-]+$/,
                        message: "Slug can only contain lowercase letters, numbers, and hyphens",
                      },
                    })}
                  />
                  {errors.slug && (
                    <p className="text-sm text-destructive">{errors.slug.message}</p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    URL-friendly version of the name
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    placeholder="Describe this category..."
                    rows={4}
                    {...register("description")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="parentId">Parent Category</Label>
                  <Select
                    value={parentId || "none"}
                    onValueChange={(value) => setValue("parentId", value === "none" ? "" : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent category (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None (Root Category)</SelectItem>
                      {categories
                        .filter(cat => !isEditMode || cat.id !== id) // Don't allow self as parent
                        .map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Create a subcategory by selecting a parent
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Category Image */}
            <Card>
              <CardHeader>
                <CardTitle>Category Image</CardTitle>
                <CardDescription>
                  Select or upload a category image (optional)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <input
                  type="hidden"
                  {...register("image")}
                />
                
                {image ? (
                  <div className="relative rounded-md border overflow-hidden group">
                    <img
                      src={image}
                      alt="Category preview"
                      className="w-full h-48 object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = "https://via.placeholder.com/400x300?text=Image+Error";
                      }}
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setMediaSelectorOpen(true)}
                      >
                        <ImagePlus className="h-4 w-4 mr-2" />
                        Change
                      </Button>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setValue("image", "")}
                      >
                        <X className="h-4 w-4 mr-2" />
                        Remove
                      </Button>
                    </div>
                    <p className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent text-xs text-white truncate">
                      {image}
                    </p>
                  </div>
                ) : (
                  <div
                    className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                    onClick={() => setMediaSelectorOpen(true)}
                  >
                    <ImagePlus className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">Select Category Image</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Click to browse media library
                    </p>
                  </div>
                )}

                <MediaSelector
                  open={mediaSelectorOpen}
                  onOpenChange={setMediaSelectorOpen}
                  onSelect={(media) => {
                    const selectedMedia = media as Media;
                    setValue("image", selectedMedia.url);
                  }}
                  folder="categories"
                  title="Select Category Image"
                  description="Choose an image from your media library or upload a new one"
                />
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/categories")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading} className="gap-2">
            <Save className="h-4 w-4" />
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
              ? "Update Category"
              : "Create Category"}
          </Button>
        </div>
      </form>
    </div>
  );
}
